import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { ArrowUturnLeftIcon, ArrowsRightLeftIcon, QuestionMarkCircleIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FloppyDisk } from "@phosphor-icons/react";

import Api from "../../../helpers/Api";
import { selectUser } from "../../../store/slices/userSlice";
import { montarPdfMedidas, dataUrlParaBlob } from "./relatorioMedidas";

import styles from "./styles.module.css";

const UNIDADES = ["cm", "mm", "in"];

interface EstadoRegua {
  ferramenta: number;
  calibrando: boolean;
  medindo: boolean;
  calibrado: boolean;
  fator: number | null;
  unidade: string;
  quantidade: number;
  total: number;
}

const ESTADO_INICIAL: EstadoRegua = {
  ferramenta: 0,
  calibrando: false,
  medindo: false,
  calibrado: false,
  fator: null,
  unidade: "cm",
  quantidade: 0,
  total: 0,
};

/**
 * Ferramenta de calibração/medida da imagem (apenas front-end).
 *
 * Fluxo: o usuário ativa a ferramenta, traça uma referência de medida conhecida
 * na própria imagem (ex.: altura do nariz), informa o valor real (ex.: 6 cm) e,
 * a partir daí, qualquer traço na imagem exibe a medida correspondente.
 *
 * Usada no viewer 2D e no viewer CefX - não é aplicada em 3D/DICOM.
 */
export function MeasureTool({ ocefx, infoPaciente }: any) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // o cadastro do complemento é feito no Serverodonto, então o tutorial só
  // interessa a quem tem acesso a ele: login de funcionário (hash terminando em FN)
  const { user } = useSelector(selectUser);
  const usuarios = Array.isArray(user) ? user : user ? [user] : [];
  const usuarioAtual =
    usuarios.find((u: any) => u?.idClient == queryParams.get("id")) || usuarios[0];
  const ehFuncionario = (usuarioAtual?.hash || "")
    .trim()
    .toUpperCase()
    .endsWith("FN");

  const [aberto, setAberto] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [estado, setEstado] = useState<EstadoRegua>(ESTADO_INICIAL);
  const [distanciaPendente, setDistanciaPendente] = useState<number | null>(null);
  const [valorReal, setValorReal] = useState("");
  const [unidade, setUnidade] = useState("cm");
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!ocefx) return;

    ocefx.setOnReguaChange((e: EstadoRegua) => setEstado(e));
    ocefx.setOnReguaCalibrar((dist: number) => {
      setDistanciaPendente(dist);
      setValorReal("");
      setErro("");
    });

    if (ocefx.getEstadoRegua) setEstado(ocefx.getEstadoRegua());

    return () => {
      ocefx.setOnReguaChange(null);
      ocefx.setOnReguaCalibrar(null);
      ocefx.sairModoRegua();
    };
  }, [ocefx]);

  useEffect(() => {
    if (estado.unidade) setUnidade(estado.unidade);
  }, [estado.unidade]);

  const cancelarCalibracao = useCallback(() => {
    setDistanciaPendente(null);
    setErro("");
    ocefx.cancelarCalibracaoRegua();
  }, [ocefx]);

  const confirmarCalibracao = () => {
    const ok = ocefx.confirmarCalibracaoRegua(valorReal, unidade);
    if (!ok) {
      setErro("Informe um valor maior que zero.");
      return;
    }
    setDistanciaPendente(null);
    setErro("");
  };

  // Gera o relatório (imagem medida + relação de medidas), grava como um cefxRel no
  // CefXWeb e dispara a publicação que leva o arquivo ao paciente no Serverodonto.
  const salvarNoPaciente = async () => {
    const clinica = queryParams.get("id") || ocefx?.estado?.paciente?.clinica;
    const atd = queryParams.get("atd") || ocefx?.estado?.paciente?.atd;

    if (!clinica || !atd) {
      toast.error("Não foi possível identificar o atendimento.");
      return;
    }

    setSalvando(true);

    try {
      const imagem = await ocefx.gerarImagemMedidas();

      if (!imagem) {
        toast.error("Não foi possível exportar esta imagem.");
        return;
      }

      const medidas = ocefx.getMedidasRegua();
      const pdf = await montarPdfMedidas({
        paciente: infoPaciente?.nome || infoPaciente?.paciente,
        idade: infoPaciente?.idade,
        atendimento: atd,
        imagem,
        medidas,
        escala:
          estado.fator != null
            ? `1 px = ${estado.fator.toFixed(4)} ${estado.unidade}`
            : undefined,
      });

      await Api.SalvarRelatorioMedidas(clinica, atd, pdf, dataUrlParaBlob(imagem));

      // a publicação responde 200 mesmo quando falha (isSuccess: false),
      // então o resultado precisa ser conferido no corpo
      const publicacao = await Api.GetPublicarNoServerodonto(Number(clinica), Number(atd));

      if (publicacao?.isSuccess && publicacao?.data !== false) {
        toast.success("Medidas salvas no paciente.");
      } else {
        console.warn("Falha ao publicar no Serverodonto:", publicacao);
        toast(
          "Relatório gerado, mas não foi possível publicá-lo no Serverodonto.",
          { icon: "⚠️" }
        );
      }
    } catch (erro) {
      console.error("Erro ao salvar as medidas no paciente:", erro);
      toast.error("Erro ao salvar as medidas no paciente.");
    } finally {
      setSalvando(false);
    }
  };

  const fecharPainel = () => {
    cancelarCalibracao();
    ocefx.sairModoRegua();
    setAberto(false);
  };

  if (!ocefx) return null;

  return (
    <div className={styles.wrapper}>
      {!aberto ? (
        <button
          type="button"
          className={styles.gatilho}
          title="Medir na imagem"
          onClick={() => setAberto(true)}
        >
          <ArrowsRightLeftIcon />
          Medir
        </button>
      ) : (
        <div className={styles.painel}>
          <header className={styles.cabecalho}>
            <strong>Medidas na imagem</strong>
            <div className={styles.acoesCabecalho}>
              <button
                type="button"
                className={ajudaAberta ? styles.ativo : ""}
                onClick={() => setAjudaAberta(!ajudaAberta)}
                title="Por que o relatório não aparece no perfil?"
              >
                <QuestionMarkCircleIcon />
              </button>
              <button type="button" onClick={fecharPainel} title="Fechar">
                <XMarkIcon />
              </button>
            </div>
          </header>

          {ajudaAberta && (
            <div className={styles.ajuda}>
              {ehFuncionario ? (
                <>
                  <strong>O relatório não aparece no perfil do paciente?</strong>
                  <p>
                    O arquivo é enviado ao Serverodonto, mas só é listado se o
                    complemento estiver cadastrado na clínica. Cadastre uma vez:
                  </p>
                  <ol>
                    <li>
                      No Serverodonto, abra <b>Cadastro → Complementos</b>.
                    </li>
                    <li>Clique em novo e preencha:</li>
                  </ol>
                  <ul className={styles.campos}>
                    <li>
                      Complemento: <code>cefxMed*</code>
                    </li>
                    <li>
                      Descrição: <code>Relatório de Medidas</code>
                    </li>
                    <li>
                      Extensão: <code>PDF</code>
                    </li>
                    <li>
                      WEB: <b>marcado</b> (sem isso não aparece)
                    </li>
                    <li>Trabalho: desmarcado</li>
                  </ul>
                  <ol start={3}>
                    <li>
                      Salve e clique em <b>Atualiza listagem</b>.
                    </li>
                  </ol>
                  <p>
                    O cadastro é por clínica e feito uma única vez. Não é preciso
                    reenviar nada: os relatórios já publicados aparecem em seguida.
                  </p>
                </>
              ) : (
                <p>
                  O relatório de medidas é enviado para o Serverodonto, mas só
                  aparece para o usuário após o cadastro do complemento correto.
                  Caso não esteja vendo seus relatórios de medida, entre em
                  contato com a clínica.
                </p>
              )}
            </div>
          )}

          <p className={styles.status}>
            {estado.calibrado ? (
              <>
                Calibrado:{" "}
                <b>
                  1 px ={" "}
                  {estado.fator != null ? estado.fator.toFixed(4) : "-"}{" "}
                  {estado.unidade}
                </b>
              </>
            ) : (
              "Sem calibração. Trace uma referência de medida conhecida."
            )}
          </p>

          <div className={styles.acoes}>
            <button
              type="button"
              className={estado.calibrando ? styles.ativo : ""}
              onClick={() => ocefx.setFerramentaReguaCalibrar()}
            >
              {estado.calibrado ? "Recalibrar" : "Calibrar"}
            </button>

            <button
              type="button"
              className={estado.medindo ? styles.ativo : ""}
              disabled={!estado.calibrado}
              title={
                estado.calibrado
                  ? "Traçar medidas na imagem"
                  : "Calibre a imagem antes de medir"
              }
              onClick={() => ocefx.setFerramentaReguaMedir()}
            >
              Medir
            </button>
          </div>

          <p className={styles.dica}>
            {estado.calibrando
              ? "Trace a referência (ex.: da base ao topo do nariz) e informe o valor real."
              : estado.medindo
                ? "Arraste sobre a imagem para medir. Ctrl+Z desfaz, botão direito move a imagem."
                : "Ative uma das opções acima."}
          </p>

          <button
            type="button"
            className={styles.salvar}
            onClick={salvarNoPaciente}
            disabled={!estado.calibrado || estado.quantidade === 0 || salvando}
            title={
              !estado.calibrado
                ? "Calibre a imagem antes de salvar"
                : estado.quantidade === 0
                  ? "Faça ao menos uma medida"
                  : "Enviar as medidas para o perfil do paciente"
            }
          >
            <FloppyDisk />
            {salvando ? "Salvando..." : "Salvar no paciente"}
          </button>

          <div className={styles.rodape}>
            <button
              type="button"
              onClick={() => ocefx.desfazerRegua()}
              disabled={estado.total === 0}
              title="Desfazer último traço (Ctrl+Z)"
            >
              <ArrowUturnLeftIcon /> Desfazer
            </button>
            <button
              type="button"
              onClick={() => ocefx.limparMedidasRegua()}
              disabled={estado.quantidade === 0}
              title="Remover todas as medidas"
            >
              <TrashIcon /> Limpar
            </button>
            <button
              type="button"
              onClick={() => ocefx.limparCalibracaoRegua()}
              disabled={!estado.calibrado}
              title="Descartar a calibração desta imagem"
            >
              Zerar calibração
            </button>
          </div>
        </div>
      )}

      {distanciaPendente !== null && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <h3>Medida de referência</h3>
            <p>
              Informe quanto mede, no paciente, o traço que você acabou de
              marcar.
            </p>

            <div className={styles.campo}>
              <input
                type="number"
                min="0"
                step="0.01"
                autoFocus
                value={valorReal}
                placeholder="Ex.: 6"
                onChange={(e) => setValorReal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmarCalibracao();
                  if (e.key === "Escape") cancelarCalibracao();
                }}
              />
              <select
                value={unidade}
                onChange={(e) => setUnidade(e.target.value)}
              >
                {UNIDADES.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>

            {erro && <span className={styles.erro}>{erro}</span>}

            <div className={styles.modalAcoes}>
              <button type="button" onClick={cancelarCalibracao}>
                Cancelar
              </button>
              <button
                type="button"
                className={styles.primario}
                onClick={confirmarCalibracao}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
